import React, { useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Amplify, Auth } from "aws-amplify";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import amplifyConfig from "./components/amplify-config";
import Summarizer from "./components/Summarizer";
import ReviewTranscript from "./components/ReviewTranscript";
import Login from "./components/Login";
import ReviewNote from "./components/ReviewNote";
import Oncall from "./components/Oncall";
import AddTestPatient from "./components/AddTestPatient";
import Support from "./components/Support";
import Templates from "./components/Templates";
import Workflows from "./components/Workflows";
import WorkflowsVisualization from "./components/WorkflowsVisualization";
import CustomDictionary from "./components/CustomDictionary";
import Coding from "./components/Coding";
import Home from "./components/Home";
import RegisterDoctors from "./components/RegisterDoctors";
import UpdateDoctors from "./components/UpdateDoctors";
import TranscriptionRules from "./components/TranscriptionRules";
import FineTuning from "./components/FineTuning";
import DatasetDetail from "./components/FineTuning/DatasetDetail.js";
import Evaluation from "./components/Evaluation";
import Trace from "./components/Trace";
import NoAccess from "./components/NoAccess";
import NotFound from "./components/NotFound";
import InternalExternalDashboard from "./components/InternalExternalDashboard";
import ModelEval from "./components/ModelEval";
import ExternalUsageDisplay from "./components/ExternalUsageDisplay/index.js";
import PaymentReconcile from "./components/PaymentReconcile";
import RevOps from "./components/RevOps";
import PatientManagement from "./components/PatientManagement";
import { getLoggedInUserOrg, getLoggedInUserId } from "./utils/Auth";
import RouteObserver from "./components/RouterObserver.js";
import Hallucination from "./components/Hallucination";
import EngineeringDashboard from "./components/EngineeringDashboard";
import UserFeedback from "./components/UserFeedback";
import IntegrationsOverview from "./components/Integrations";
import OncoEMR              from "./components/Integrations/OncoEMR";
import Boomerang            from "./components/Integrations/Boomerang";
import VA from "./components/VA";
import CustomerSuccessTools from "./components/CustomerSuccess/Tools";
import Karoline from "./components/Karoline";
import FoiaRequestList from "./components/Foia/FoiaRequestList";
import FoiaRequestDetail from "./components/Foia/FoiaRequestDetail";
import FoiaNewRequest from "./components/Foia/FoiaNewRequest";
import FoiaEngineeringTasks from "./components/Foia/FoiaEngineeringTasks";
import FinancialReviewPage from "./components/FinancialReviewPage";
import VAEvalLanding from "./components/VAEval";
import QAEvalLanding from "./components/QAEval";
import McRunsPage from "./components/MC/McRunsPage";
import McRunDetailPage from "./components/MC/McRunDetailPage";
import McPlaybooksPage from "./components/MC/McPlaybooksPage";
import McMemoryPage from "./components/MC/McMemoryPage";
import McIssuesPage from "./components/MC/McIssuesPage";
import McSettingsPage from "./components/MC/McSettingsPage";
import { isCapabilityEnabled, isAnyCapabilityEnabled } from "./config/capabilities";

// Configure AWS Amplify
Amplify.configure(amplifyConfig);
Auth.configure(amplifyConfig);

const McIndexRedirect = () => <Navigate to="/mc/runs" replace />;

const routes = [
  { path: "/login", component: Login, private: false },
  { path: "/", component: Home, private: true, capability: "home" },
  { path: "/oncall", component: Oncall, private: true, capability: "oncall" },
  { path: "/add-test-patient", component: AddTestPatient, private: true, capability: "addTestPatient" },
  { path: "/support/*", component: Support, private: true, capability: "customerSupport" },
  { path: "/hallucination", component: Hallucination, private: true, capability: "hallucination" },
  { path: "/review-note", component: ReviewNote, private: true, capability: "reviewNote" },
  { path: "/review-transcript", component: ReviewTranscript, private: true, capability: "reviewTranscript" },
  { path: "/summarize", component: Summarizer, private: true, capability: "allJobs" },
  { path: "/templates", component: Templates, private: true, capability: "templates" },
  { path: "/templates/:templateName", component: Templates, private: true, capability: "templates" },
  { path: "/workflows", component: Workflows, private: true, capability: "workflows" },
  { path: "/workflows/visualization", component: WorkflowsVisualization, private: true, capability: "workflowsVisualization" },
  { path: "/workflows/visualization/:view", component: WorkflowsVisualization, private: true, capability: "workflowsVisualization" },
  { path: "/workflows/visualization/:view/:doctorId", component: WorkflowsVisualization, private: true, capability: "workflowsVisualization" },
  { path: "/custom-dictionary", component: CustomDictionary, private: true, capability: "customDictionary" },
  { path: "/coding", component: Coding, private: true, capability: "coding" },
  { path: "/register-doctors", component: RegisterDoctors, private: true, capability: "registerDoctors" },
  { path: "/update-doctors", component: UpdateDoctors, private: true, capability: "updateDoctors" },
  { path: "/transcription-rules", component: TranscriptionRules, private: true, capability: "transcriptionRules" },
  { path: "/fine-tuning", component: FineTuning, private: true, capability: "mlWorkflows" },
  { path: "/evaluation", component: Evaluation, private: true, capability: "evaluation" },
  { path: "/datasets/:datasetId", component: DatasetDetail, private: true, capability: "datasets" },
  { path: "/trace", component: Trace, private: true, capability: "trace" },
  { path: "/argus", component: EngineeringDashboard, private: true, capability: "argus" },
  { path: "/patient-management", component: PatientManagement, private: true, capability: "patientManagement" },
  { path: "/user-feedback", component: UserFeedback, private: true, capability: "userFeedback" },
  {
    path: "/internal-external-dashboard",
    component: InternalExternalDashboard,
    private: true,
    capability: "externalView",
  },
  { path: "/eval", component: ModelEval, private: true, capability: "modelEval" },
  { path: "/external-usage", component: ExternalUsageDisplay, private: true, capability: "externalUsage" },
  { path: "/payment-reconcile", component: PaymentReconcile, private: true, capability: "paymentReconcile" },
  {
    path: "/revops",
    component: RevOps,
    private: true,
    capability: "revops",
  },
  { path: "/integrations", component: IntegrationsOverview, private: true, capability: "integrations" },
  { path: "/oncoemr", component: OncoEMR, private: true, capability: "oncoemr" },
  { path: "/boomerang", component: Boomerang, private: true, capability: "boomerang" },
  { path: "/va-eval", component: VAEvalLanding, private: true, capability: "vaEval" },
  { path: "/qa-eval", component: QAEvalLanding, private: true, capability: "qaEval" },
  { path: "/va-register", component: VA, private: true, capability: "vaRegister" },
  { path: "/cs-tools", component: CustomerSuccessTools, private: true, capability: "csTools" },
  { path: "/karoline", component: Karoline, private: true, capability: "karoline" },
  { path: "/foia", component: FoiaRequestList, private: true, capability: "foiaTracking" },
  { path: "/foia/new", component: FoiaNewRequest, private: true, capability: "foiaTracking" },
  { path: "/foia/requests/:requestId", component: FoiaRequestDetail, private: true, capability: "foiaTracking" },
  { path: "/foia/engineering", component: FoiaEngineeringTasks, private: true, capability: "foiaTracking" },
  { path: "/financial-review", component: FinancialReviewPage, private: true, capability: ["financialReview", "commercialFinancialReview"] },
  { path: "/mc", component: McIndexRedirect, private: true, capability: "mc" },
  { path: "/mc/runs", component: McRunsPage, private: true, capability: "mc" },
  { path: "/mc/runs/:runId", component: McRunDetailPage, private: true, capability: "mc" },
  { path: "/mc/playbooks", component: McPlaybooksPage, private: true, capability: "mc" },
  { path: "/mc/memory", component: McMemoryPage, private: true, capability: "mc" },
  { path: "/mc/issues", component: McIssuesPage, private: true, capability: "mc" },
  { path: "/mc/settings", component: McSettingsPage, private: true, capability: "mc" },
  { path: "*", component: NotFound, private: true },
];

export default function App() {
  const VA_EVAL_ORG = "VA-eval";
  const VA_EVAL_PATH = "/va-eval";
  const KNOWTEX_ORG = "Knowtex";
  const [isAuthenticated, setAuth] = useState(null);
  const [userOrg, setUserOrg] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          // Try to validate the token
          try {
            await Auth.currentSession();
            checkAuth(); // Token is valid
          } catch (error) {
            console.error("Invalid token on load: ", error);
            localStorage.removeItem("token");
            setAuth(false);
          }
        } else {
          setAuth(false);
        }
      } catch (error) {
        console.error("Error validating token:", error);
        localStorage.removeItem("token");
        setAuth(false);
      }
    })();
  }, []);

  useEffect(() => {
    checkAuth();

    if (isAuthenticated) {
      getUserOrg();
      getUserId();
    }
  }, [isAuthenticated]);

  // The provider stays mounted on the login screen, so a logout has to drop the cache
  // itself. Doing it here covers every logout button and a token rejected on load.
  const wasAuthenticated = useRef(false);
  useEffect(() => {
    if (wasAuthenticated.current && !isAuthenticated) queryClient.clear();
    wasAuthenticated.current = !!isAuthenticated;
  }, [isAuthenticated]);

  const checkAuth = () => {
    const accessToken = localStorage.getItem("token");
    setAuth(accessToken ? true : false);
  };

  const getUserOrg = async () => {
    setUserOrg(await getLoggedInUserOrg());
  };

  const getUserId = async () => {
    setUserId(await getLoggedInUserId());
  };

  return (
    isAuthenticated !== null && (
      <QueryClientProvider client={queryClient}>
        <Router>
          <RouteObserver />
          <Routes>
            {routes
              .filter(
                ({ capability }) =>
                  !capability || isAnyCapabilityEnabled(capability)
              )
              .map(({ path, component: Component, private: isPrivate }) => (
              <Route
                key={path}
                path={path}
                element={(() => {
                  const isVaEvalUser = userOrg === VA_EVAL_ORG;
                  const isKnowtexUser = userOrg === KNOWTEX_ORG;

                  if (isAuthenticated !== isPrivate) {
                    return (
                      <Navigate
                        to={
                          isPrivate
                            ? "/login"
                            : isVaEvalUser && isCapabilityEnabled("vaEval")
                              ? VA_EVAL_PATH
                              : "/"
                        }
                        state={
                          isPrivate
                            ? { from: window.location.pathname + window.location.search }
                            : undefined
                        }
                      />
                    );
                  }

                  if (
                    isAuthenticated &&
                    isVaEvalUser &&
                    isCapabilityEnabled("vaEval") &&
                    path !== VA_EVAL_PATH
                  ) {
                    return <Navigate to={VA_EVAL_PATH} replace />;
                  }

                  if (
                    path !== "/login" &&
                    path !== "/" &&
                    isAuthenticated &&
                    userOrg !== null &&
                    !isKnowtexUser &&
                    !isVaEvalUser
                  ) {
                    return <NoAccess setAuth={setAuth} />;
                  }

                  return (
                    <Component
                      setAuth={setAuth}
                      userOrg={userOrg}
                      setUserOrg={setUserOrg}
                      userId={userId}
                    />
                  );
                })()}
              />
            ))}
          </Routes>
        </Router>
      </QueryClientProvider>
    )
  );
}
